import React from 'react';
import { memoize } from 'lodash';
import styled from '@emotion/styled';
import { ProductCatalogueItem, PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import SectionMessage, { Appearance, SectionMessageAction } from '@atlaskit/section-message';
import {
  renderPromotionOption,
  ColGroup,
  CustomDynamicResultTable,
  EntitlementIcon,
  TableBody,
  TableHead,
} from '@css-xp/base';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { AppPromoCodeDetails } from '@css-xp/ppc-checkout/src/types';
import { token } from '@atlaskit/tokens';
import { N500 } from '@atlaskit/theme/colors';

import { TABLE_HEADER, ManageAppsPromocode, usePartnerCatalogueContext } from '@css-xp/ppc-checkout';
import { emptyStateProp, errorStateProp, NoData } from '@css-xp/ppc-common';
import { PromocodeDetails } from '@css-xp/ppc-checkout/src/manage-apps-promocode/manage-apps-promocode';
import { AppPromoCodeDetailsProps } from '../../../product-line-items/src/components/new-app-purchase-modal';
import { isStatusPageAddOnProduct } from '@css-xp/ppc-utils';
import { STATUSPAGE_ADDON_PRODUCT_ID } from '@css-xp/ppc-constants';

const StyledManagePromoContainer = styled.div`
  .promotion-view-section {
    .manage-apps-promotions-container {
      margin-top: 0px;
      > div {
        .manage-apps-promotions-input-search {
          width: 240px;
        }
      }
    }
  }
`;

const StyledSectionMessageContainer = styled.div`
  > section {
    >div: nth-child(1) {
      align-items: center;
    }
    >div: nth-child(2) {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
`;

type CatalogAppDetail = PartnerProductCatalogueItem & {
  vendorName: string;
};

const createHead = memoize(() => {
  return [
    { name: TABLE_HEADER.PRODUCT, content: TABLE_HEADER.PRODUCT, customClassNames: 'col-50' },
    { name: TABLE_HEADER.VENDOR_PROMOTION, content: TABLE_HEADER.VENDOR_PROMOTION, customClassNames: 'col-50' },
  ];
});
export const AddVendorPromotionTable = ({
  appsList,
  setAppPromotionIdsDetails,
  setCheckedProductsList,
}: {
  appsList: any;
  setAppPromotionIdsDetails?: (_promotionCodeDetails: AppPromoCodeDetailsProps) => void;
  setCheckedProductsList?: (checkedProductsList: PartnerProductCatalogueItem[]) => void;
}) => {
  const { removeAppsPromotionId } = usePartnerCatalogueContext();
  const [appsPromocodeDetails, setAppsPromocodeDetails] = React.useState<Map<string, PromocodeDetails>>(new Map());
  const [promotionDetails, setPromotionDetails] = React.useState<any>({});
  const [showApplyFieldOnRemove, setShowApplyFieldOnRemove] = React.useState<boolean>(false);

  const createTableHead = React.useCallback(() => {
    return createHead();
  }, []);

  const addPromocodeDetails = (productId: string, promocodeDetail: PromocodeDetails) => {
    const newMap = new Map(appsPromocodeDetails);
    newMap.set(productId, promocodeDetail);
    setAppsPromocodeDetails(newMap);
  };

  const removePromocodeDetails = (productId: string) => {
    appsPromocodeDetails.delete(productId);
    setAppsPromocodeDetails(new Map(appsPromocodeDetails));
    setAppPromotionIdsDetails?.({
      productId,
      promotionDetails: undefined,
    });
    setPromotionDetails({
      productId,
      promotionDetails: undefined,
    });

    removeAppsPromotionId(productId);
    setShowApplyFieldOnRemove(true);
  };

  const renderPromotionDetails = (productId: string, promotion: AppPromoCodeDetails) => {
    return (
      <StyledSectionMessageContainer>
        <SectionMessage
          appearance="success"
          actions={[
            <SectionMessageAction
              onClick={() => {
                removePromocodeDetails(productId);
              }}
              key={`remove-${productId}`}
            >
              Remove
            </SectionMessageAction>,
          ]}
        >
          <div>{renderPromotionOption(promotion, { showBenefitNextLine: false })}</div>
        </SectionMessage>
      </StyledSectionMessageContainer>
    );
  };

  const createTableRows = React.useCallback(() => {
    return appsList?.map((linkedEntitlements: CatalogAppDetail, index: number) => {
      const name = linkedEntitlements?.name ?? '';
      const vendorName = linkedEntitlements?.vendorName ?? '';
      const rowData = createHead().map((column) => {
        switch (column.content) {
          case TABLE_HEADER.PRODUCT:
            return (
              <td className="catalog-table-product">
                <Button
                  appearance="link"
                  onClick={() => {
                    setCheckedProductsList?.(
                      appsList?.filter((app: PartnerProductCatalogueItem) => app.id !== linkedEntitlements?.id)
                    );
                  }}
                >
                  <CrossIcon size="small" label="Close Modal" primaryColor={token('color.text.subtle', N500)} />
                </Button>
                <EntitlementIcon
                  productId={
                    // this is required for statuspage addons to show correct icon
                    isStatusPageAddOnProduct(linkedEntitlements?.id)
                      ? STATUSPAGE_ADDON_PRODUCT_ID
                      : linkedEntitlements?.id
                  }
                  className="avatar medium"
                  isCloud={true}
                  //@ts-ignore
                  logoId={linkedEntitlements?.logoId}
                />
                <div className="catalog-table-product-detail">
                  <div className="catalog-table-product-detail-name" title={name}>
                    {name}
                  </div>
                  <div className="catalog-table-product-detail-vendorName">{vendorName}</div>
                </div>
              </td>
            );
          case TABLE_HEADER.VENDOR_PROMOTION:
            return (
              <td>
                {linkedEntitlements?.isApp ? (
                  <StyledManagePromoContainer>
                    <ManageAppsPromocode
                      productId={linkedEntitlements?.id}
                      addPromocodeDetails={addPromocodeDetails}
                      removePromocodeDetails={removePromocodeDetails}
                      isPromotionDetailHidden={true}
                      setAppsPromotionDetails={setAppPromotionIdsDetails}
                      setPromotionDetails={setPromotionDetails}
                      hideRemovePromotionCTA={true}
                      showApplyFieldOnRemove={showApplyFieldOnRemove}
                      setShowApplyFieldOnRemove={setShowApplyFieldOnRemove}
                      hideSuccessIconOnValidPromotion={true}
                    />
                  </StyledManagePromoContainer>
                ) : (
                  <NoData />
                )}
              </td>
            );
        }
      });
      return (
        <>
          <tr key={`row-${index}-${name}`} data-testId={`row-${index}-${linkedEntitlements?.id}`}>
            {rowData}
          </tr>
          {promotionDetails?.productId === linkedEntitlements.id && promotionDetails?.promotionDetails && (
            <tr>
              <td colSpan={createHead().length}>
                {renderPromotionDetails(promotionDetails?.productId, promotionDetails?.promotionDetails)}
              </td>
            </tr>
          )}
        </>
      );
    });
  }, [appsList, promotionDetails]);

  return (
    <CustomDynamicResultTable
      className="products-list-table"
      loadingSpinnerSize="large"
      isLoading={false}
      testId="product-catalog-list"
      error=""
      noResults={appsList.length === 0}
      boxShadow={false}
      emptyStateProps={emptyStateProp}
      errorStateProps={errorStateProp}
    >
      <ColGroup columns={createTableHead()} />
      <TableHead columns={createTableHead()} />
      <TableBody>{createTableRows()}</TableBody>
    </CustomDynamicResultTable>
  );
};
